import type { GuidedEditPayload } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import type { BudgetData } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor/BudgetSelector";
import type {
  GuidedEditPayloadBudgetDisplay,
  GuidedEditUpdateBudget,
} from "@common/interfaces/adsManagerGuidedEdit";
import { wireGuidedEditBudgetFromFormDollars } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/wireGuidedEditBudgetFromForm";
import { wireScheduleToEventWall } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/eventWallTime";
import type { ScheduleInput } from "../components/EditScheduleModal";

/**
 * Unwraps API payloads when the REST client returns the Lambda envelope (`{ response, … }`) more
 * than once, or `{ data: … }` variants — so we always read `budget` from the real guided-edit body.
 */
export function coerceGuidedEditPayload(raw: unknown): GuidedEditPayload {
  let cur: unknown = raw;
  for (let i = 0; i < 6; i += 1) {
    if (!cur || typeof cur !== "object") break;
    const o = cur as Record<string, unknown>;
    if (
      "budget" in o &&
      o.budget != null &&
      typeof o.budget === "object"
    ) {
      return cur as GuidedEditPayload;
    }
    const next =
      o.response !== undefined
        ? o.response
        : o.data !== undefined
          ? o.data
          : undefined;
    if (next === undefined) break;
    cur = next;
  }
  throw new Error("Guided edit payload missing budget");
}

/**
 * True when GET guided-edit budget is phased. Accepts missing `type` if the wire shape is
 * clearly phased (segments, no top-level minor `amount`) so the modal does not fall back to lifetime.
 */
export function isGuidedEditPayloadBudgetScheduled(
  budget: GuidedEditPayloadBudgetDisplay | null | undefined
): budget is Extract<GuidedEditPayloadBudgetDisplay, { type: "scheduled" }> {
  if (!budget || typeof budget !== "object") return false;
  if ((budget as { type?: string }).type === "scheduled") return true;
  const b = budget as Record<string, unknown>;
  const segs = b.segments;
  const hasSegments = Array.isArray(segs) && segs.length > 0;
  const amount = b.amount;
  const hasNumericWireAmount =
    typeof amount === "number" && Number.isFinite(amount);
  return Boolean(hasSegments && !hasNumericWireAmount);
}

/** Initial {@link BudgetData} for the Engage “Edit budget” modal when the campaign uses phased budgets. */
export function buildScheduledBudgetDataForEditModal(params: {
  eventTimezone: string;
  segments: Array<{
    time_start: string;
    time_end: string;
    /** Minor units (e.g. cents), same as GET guided-edit segment `amount`. */
    amountMinor: number;
    currency: string;
  }>;
}): BudgetData {
  const tz = params.eventTimezone;
  return {
    type: "scheduled",
    amount: { amount: null, currency: "USD" },
    base: {
      type: "lifetime",
      amount: { amount: null, currency: "USD" },
    },
    segments: params.segments.map((s) => ({
      time_start: wireScheduleToEventWall(s.time_start, tz),
      time_end: wireScheduleToEventWall(s.time_end, tz),
      amount: {
        amount: s.amountMinor / 100,
        currency: s.currency,
      },
    })),
  };
}

type BasePayload = {
  campaign_id: string;
  adset_id: string;
};

export function buildBudgetUpdatePayload(params: {
  campaign_id: string;
  adset_id: string;
  budget: BudgetData;
  eventTimezone?: string;
}): {
  campaign_id: string;
  adset_id: string;
  budget: GuidedEditUpdateBudget;
} {
  const { campaign_id, adset_id, budget, eventTimezone } = params;
  return {
    campaign_id,
    adset_id,
    budget: wireGuidedEditBudgetFromFormDollars(budget, eventTimezone),
  };
}

export function buildScheduleUpdatePayload(params: {
  campaign_id: string;
  adset_id: string;
  schedule: ScheduleInput;
}) {
  const { campaign_id, adset_id, schedule } = params;
  return {
    campaign_id,
    adset_id,
    schedule,
  };
}

export function buildCreativeUpdatePayload(params: BasePayload & {
  creatives: GuidedEditPayload["creatives"];
  creativeIndex: number;
  creative: GuidedEditPayload["creatives"][number];
}) {
  const { campaign_id, adset_id, creatives, creativeIndex, creative } = params;
  const nextCreatives = [...creatives];
  nextCreatives[creativeIndex] = creative;
  return {
    campaign_id,
    adset_id,
    creatives: nextCreatives,
  };
}
